import { useMemo, useCallback } from 'react';

import {
  ColumnListKey,
  ColumnPreference,
  useGetColumnPreferencesQuery,
  useUpdateColumnPreferencesMutation,
} from 'src/services/settings/column-preferences.service';

import { CUSTOM_FIELD_COLUMN_PREFIX } from './custom-field-columns.types';
import { ColumnDef, PickerColumn, resolveColumns, buildPickerModel } from './data-list.types';

/**
 * Ties a per-entity column registry to the user's saved layout (JT1-1528): resolves the visible,
 * ordered columns for DataList, builds the full picker model, and persists changes. The picker only
 * needs to hand back the non-fixed columns in the desired order with their visibility.
 */
export function useColumnPreferences<TRow>(list: ColumnListKey, registry: ColumnDef<TRow>[]) {
  const { data, isLoading } = useGetColumnPreferencesQuery(list);
  const [updatePreferences] = useUpdateColumnPreferencesMutation();

  const preferences = data?.columns ?? null;

  const columns = useMemo(() => resolveColumns(registry, preferences), [registry, preferences]);
  const pickerModel = useMemo(() => buildPickerModel(registry, preferences), [registry, preferences]);

  const save = useCallback(
    (model: PickerColumn[]) => {
      // Persist every non-fixed column in the chosen order with its visibility; fixed columns are
      // always shown and don't need storing.
      const columnsToSave: ColumnPreference[] = model
        .filter((col) => !col.fixed)
        .map((col) => ({ key: col.key, visible: col.visible }));

      // Carry forward saved custom-field columns the registry doesn't currently contain.
      //
      // Those columns come from an async config fetch, so the registry can legitimately be missing
      // them at the moment the user hits Apply — and persisting only the model would then wipe
      // every cf: preference they had. resolveColumns already ignores a preference naming a key
      // that no longer exists, so carrying a stale one forward is self-healing; a field that was
      // genuinely deleted just stops resolving.
      //
      // Scoped to the cf: prefix on purpose: static columns removed from a registry should still
      // drop out, and the stored blob must not grow without bound.
      const inModel = new Set(columnsToSave.map((col) => col.key));
      const preserved = (preferences ?? []).filter(
        (pref) => !inModel.has(pref.key) && pref.key.startsWith(CUSTOM_FIELD_COLUMN_PREFIX)
      );

      return updatePreferences({ list, columns: [...columnsToSave, ...preserved] })
        .unwrap()
        .catch(() => undefined);
    },
    [list, preferences, updatePreferences]
  );

  return { columns, pickerModel, save, isLoading };
}
